import { useEffect, useRef } from 'react';

import scheduleOnPaint from '@atlaskit/react-ufo/schedule-on-paint';

import {
	ASYNC_QUERY,
	recordQueryDataAvailable,
	recordResultsRendered,
} from '../../../common/utils/perf/query-layer-timings';

/**
 * Measures two frontend-layer segments of vc90 for the async results query:
 * - `asyncQueryDataAvailableAt` — when the search request finishes, including Relay
 *   normalisation and React's render scheduling delay.
 * - `asyncQueryRenderedAt` — when the search results actually paint to the UI.
 */
export function useAfterQueryPerformanceTiming<Edges>(edges: Edges): void {
	// This ref captures the time after we receive the search results response, including Relay
	// normalisation and React's scheduling delay. We key it on `edges` to detect when the
	// search results have actually changed. `recordQueryDataAvailable` only records this
	// timestamp if it's settling a dispatch that's still pending — so a render triggered by
	// something else, like pagination, is correctly ignored.
	const dataAvailableRef = useRef<{ edges: Edges; time: number } | null>(null);
	if (dataAvailableRef.current?.edges !== edges) {
		dataAvailableRef.current = { edges, time: performance.now() };
	}

	// This ref ensures we only record the render time once per render, even if React invokes
	// this effect twice for it (e.g. under StrictMode).
	const renderScheduledForRef = useRef<Edges | null>(null);

	useEffect(() => {
		// Only bounds the query for the dispatch that opened it — a later render reaches this
		// same effect with new edges but no dispatch pending, and is correctly ignored.
		if (dataAvailableRef.current?.edges !== edges) {
			return;
		}
		const recorded = recordQueryDataAvailable(ASYNC_QUERY, dataAvailableRef.current.time);
		if (!recorded) {
			return; // pagination, or otherwise not this dispatch's own settle
		}

		if (renderScheduledForRef.current === edges) {
			return; // already scheduled for this exact render (e.g. StrictMode double-invoke)
		}
		renderScheduledForRef.current = edges;

		// `scheduleOnPaint` has no cancellation handle of its own, so we track cancellation
		// locally: this flag stops us recording the render timestamp if this render has since
		// been superseded (a newer dispatch, or pagination) or the component has unmounted.
		let cancelled = false;
		scheduleOnPaint(() => {
			if (cancelled) {
				return;
			}
			recordResultsRendered(ASYNC_QUERY, performance.now());
		});

		return () => {
			cancelled = true;
		};
	}, [edges]);
}
